import {
  setTimerStatus,
  setGameStatus,
  setTimerConfig,
  TimerStatus,
  TimerConfig,
  PhaseStatus,
  RoundStatus,
  PhaseType,
  ActivityState,
} from '@/redux/slices/timerConfigurationSlice'
import { GameRoomStatus } from '@/redux/types/room'
import { AppDispatch } from '@/redux/store'
import { toast } from 'sonner'
import store from '@/redux/store'
import { MessageProcessor } from '../websocketProcessorTypes'
import {
  TimerMode
} from '@/components/settings/timer-configuration/types'
import { getRoomDetails } from '@/redux/asyncThunks/room'
import { mergeTimerAlertsFromMessage, getManualModeMinSecondsRemaining, maybeToastTimeThresholdAlerts, manualModeAlertContextKey, manualModePhaseLabelForAlert, getPhaseName, getCurrentRoomIdFromStore} from '@/utils/timerProcessorsUtils'

let lastKnownRoundNumber: number | null = null

export const timerProcessors: MessageProcessor[] = [
  {
    type: 'timer_update',
    handler: (data: any) => {
      try {
        const dispatch = store.dispatch as AppDispatch

        // Prior status for omitted-field guards below: `setTimerStatus` replaces
        // the whole status object, so fields OMITTED from a partial frame
        // (gameStatus / phaseData / roundData) fall back to the previous value
        // instead of being wiped.
        const prevStatus = store.getState().timerConfiguration.status

        const timerStatus: Partial<TimerStatus> = {
          mode: data.game_mode || '',
          currentRound: data.current_round ?? prevStatus?.currentRound ?? 0,
          totalRounds: data.total_rounds ?? prevStatus?.totalRounds ?? 1,
          currentPhase: data.current_phase || '',
          timeRemaining:
            typeof data.time_remaining === 'number' ? data.time_remaining : 0,
          canGoPreviousPhase: Boolean(data.can_go_previous_phase),
          isRunning:
            typeof data.time_remaining === 'number'
              ? data.time_remaining > 0
              : false,
          // Authoritative game-level status (int). Same omitted-field guard as
          // the round counters above.
          gameStatus: data.game_status ?? prevStatus?.gameStatus,
        }

        if (data.game_mode === TimerMode.MANUAL_MODE) {
          if (data.phase_data && Array.isArray(data.phase_data)) {
            const phasesData: PhaseStatus[] = data.phase_data.map(
              (phase: any) => ({
                id: phase.id,
                phaseType: phase.phase_type as PhaseType,
                status: phase.status as ActivityState,
                duration: phase.duration,
                startTime: phase.start_time,
                endTime: phase.end_time,
                isActive: phase.is_active,
                isInfinite: phase.is_infinite,
              })
            )
            timerStatus.phasesData = phasesData
          }
        } else {
          if (data.phase_data) {
            const phaseData: PhaseStatus = {
              id: data.phase_data.id,
              phaseType: data.phase_data.phase_type as PhaseType,
              status: data.phase_data.status as ActivityState,
              duration: data.phase_data.duration,
              startTime: data.phase_data.start_time,
              endTime: data.phase_data.end_time,
              isActive: data.phase_data.is_active,
              isInfinite: data.phase_data.is_infinite,
            }
            timerStatus.phaseData = phaseData
          } else if (
            prevStatus?.phaseData &&
            prevStatus.currentRound === timerStatus.currentRound
          ) {
            // Same omitted-field guard as the round counters: a frame without
            // phase_data (same round) must not wipe the live phase.
            timerStatus.phaseData = prevStatus.phaseData
          }
        }

        if (data.round_data) {
          const roundData: RoundStatus = {
            id: data.round_data.id,
            roundNumber: data.round_data.round_number,
            status: data.round_data.status as ActivityState,
            startTime: data.round_data.start_time,
            endTime: data.round_data.end_time,
          }
          timerStatus.roundData = roundData
        } else if (
          prevStatus?.roundData &&
          prevStatus.roundData.roundNumber === timerStatus.currentRound
        ) {
          // A frame without round_data (same round) previously wiped roundData,
          // resetting roundStatus to null — which flipped a paused game's button
          // back to "Pause". Carry the prior value; a real round change (or a
          // reset to round 0) still replaces/drops it.
          timerStatus.roundData = prevStatus.roundData
        }

        const alerts = mergeTimerAlertsFromMessage(data)
        const existingCfg = store.getState().timerConfiguration.config
        if (existingCfg && data.alerts && typeof data.alerts === 'object') {
          dispatch(
            setTimerConfig({
              ...existingCfg,
              alerts,
            })
          )
        }

        const roundForToast = timerStatus.currentRound || 0

        if (
          data.game_mode === TimerMode.MANUAL_MODE &&
          timerStatus.phasesData?.length
        ) {
          const phases = timerStatus.phasesData
          const currentSeconds = getManualModeMinSecondsRemaining(phases)
          maybeToastTimeThresholdAlerts({
            alerts,
            contextKey: manualModeAlertContextKey(phases),
            currentSeconds,
            phaseLabel: manualModePhaseLabelForAlert(phases),
            currentRound: roundForToast,
          })
        } else if (data.game_mode !== TimerMode.MANUAL_MODE) {
          const timeRem =
            typeof data.time_remaining === 'number' ? data.time_remaining : null
          if (timeRem !== null && timeRem >= 0) {
            const phaseId = timerStatus.phaseData?.id
            const contextKey =
              phaseId != null ? `phase-${phaseId}` : 'no-phase'
            const phaseLabel = timerStatus.phaseData
              ? getPhaseName(timerStatus.phaseData.phaseType)
              : 'this phase'
            maybeToastTimeThresholdAlerts({
              alerts,
              contextKey,
              currentSeconds: timeRem,
              phaseLabel,
              currentRound: roundForToast,
            })
          }
        }

        if (data.show_alert) {
          const roomId = getCurrentRoomIdFromStore()
          if (roomId) {
            dispatch(getRoomDetails(roomId))
          }

          if (data.game_mode === TimerMode.MANUAL_MODE && timerStatus.phasesData) {
            const activePhases = timerStatus.phasesData.filter(
              (phase) => phase.isActive
            )
            if (activePhases.length > 0) {
              toast.info(`Round ${timerStatus.currentRound} started`, {
                duration: 5000,
              })
            }
          } else if (timerStatus.phaseData) {
            const phaseName = getPhaseName(timerStatus.phaseData.phaseType)
            toast.info(
              `Round ${timerStatus.currentRound}: ${phaseName} has started`,
              {
                duration: 5000,
              }
            )
          }
        }

        const roundNumber =
          typeof data.current_round === 'number' ? data.current_round : null
        if (roundNumber !== null && roundNumber !== lastKnownRoundNumber) {
          lastKnownRoundNumber = roundNumber
          const roomId = getCurrentRoomIdFromStore()
          if (roomId) {
            dispatch(getRoomDetails(roomId))
          }
        }

        dispatch(setTimerStatus(timerStatus as TimerStatus))
      } catch (error) {
        console.error('Error processing timer update:', error)
      }
    },
  },

  {
    type: 'timer_config',
    handler: (data: any) => {
      try {
        const dispatch = store.dispatch as AppDispatch

        if (data.config) {
          const config: TimerConfig = data.config

          dispatch(setTimerConfig(config))

          if (data.show_success) {
            toast.success('Timer configuration loaded')
          }
        }
      } catch (error) {
        console.error('Error processing timer configuration:', error)
      }
    },
  },

  {
    type: 'timer_config_saved',
    handler: (data: any) => {
      try {
        if (data.success) {
          toast.success('Timer configuration saved successfully')
        } else if (data.error) {
          toast.error(`Failed to save timer configuration: ${data.error}`)
        }
      } catch (error) {
        console.error(
          'Error processing timer configuration saved message:',
          error
        )
      }
    },
  },

  {
    type: 'timer_control_result',
    handler: (data: any) => {
      try {
        const dispatch = store.dispatch as AppDispatch
        const roomId = getCurrentRoomIdFromStore()

        const refreshRoomDetails = () => {
          if (roomId) {
            dispatch(getRoomDetails(roomId))
          }
        }

        if (data.success) {
          if (data.operation === 'start_round') {
            toast.success('Started next round')
            refreshRoomDetails()
          } else if (data.operation === 'start_next_round') {
            refreshRoomDetails()
          } else if (data.operation === 'end_round') {
            toast.success('Ended current round')
            refreshRoomDetails()
          } else if (data.operation === 'end_phase') {
            toast.success('Ended current phase')
            refreshRoomDetails()
          } else if (data.operation === 'phase_transition') {
            refreshRoomDetails()
          } else if (data.operation === 'previous_phase') {
            refreshRoomDetails()
          } else if (data.operation === 'pause_game') {
            toast.success('Game paused')
            // Flip the acting client immediately (others get the broadcast
            // timer_update); keep currentRoom.status fresh for control gating.
            dispatch(setGameStatus(GameRoomStatus.PAUSED))
            refreshRoomDetails()
          } else if (data.operation === 'resume_game') {
            toast.success('Game resumed')
            dispatch(setGameStatus(GameRoomStatus.IN_PROGRESS))
            refreshRoomDetails()
          } else if (data.operation === 'start_phase') {
            toast.success('Started phase')
            refreshRoomDetails()
          } else if (data.operation === 'game_completed') {
            refreshRoomDetails()
          } else if (data.operation === 'end_simulation') {
            dispatch(getRoomDetails(data.gameroom))
          }
        } else if (data.error) {
          toast.error(`Failed to perform operation: ${data.error}`)
        }
      } catch (error) {
        console.error('Error processing timer control result:', error)
      }
    },
  },
]
